import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

// Normalize the various user payload shapes the API returns.
// /auth/me returns { user }, signup/login return { token, user }.
function unwrapUser(data) {
  if (!data) return null;
  return data.user || data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get('/auth/me')
      .then((res) => setUser(unwrapUser(res.data)))
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, []);

  const persist = useCallback((data) => {
    if (data?.token) localStorage.setItem('token', data.token);
    const u = unwrapUser(data);
    setUser(u);
    return u;
  }, []);

  const login = useCallback(
    async (email, password) => {
      const res = await api.post('/auth/login', { email, password });
      return persist(res.data);
    },
    [persist]
  );

  const signup = useCallback(
    async (name, email, password) => {
      const res = await api.post('/auth/signup', { name, email, password });
      return persist(res.data);
    },
    [persist]
  );

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
